<template>
    <UDropdown 
        :items="items" 
        :popper="{ arrow: true, placement: 'top' }" 
        :ui="{ 
            width: 'w-40',
            padding: 'p-1.5 space-y-1.5',
            item: {
                base: 'overflow-hidden !text-white',
                padding: 'p-0'
            }
        }">
        <UButton
            block
            :ui="{ rounded: 'rounded-full' }"
            icon="i-mdi-phone-hangup"
            size="xl"
            class="w-16 items-center dark:text-white dark:bg-red-500 dark:hover:bg-red-600"
        />

        <template #item="{ item }">
            <div :class="item.color" class="w-full flex items-center py-2 px-2.5 space-x-2.5">
                <UIcon :name="item.icon" class="shrink-0 size-5 text-white" />
                <span class="truncate font-medium">{{ item.label }}</span>
            </div>
        </template>
    </UDropdown>
</template>

<script setup lang="ts">
import { CallStatus } from '~/types/call'

const emit = defineEmits<{
    'confirm': [void]
    'report-incomplete': [{ reportId: string | null; missing: string[] }]
}>()

const props = defineProps<{
    callId: string
}>()

const toast = useToast()

const items = [[
    {
        label: 'Resolved',
        icon: 'i-ph-check-circle-duotone',
        color: 'bg-emerald-600 hover:bg-emerald-500',
        click: () => onEndCall(CallStatus.resolved),
    },
    {
        label: 'Unresolved',
        icon: 'i-ph-x-circle-duotone',
        color: 'bg-orange-500 hover:bg-orange-400',
        click: () => onEndCall(CallStatus.unresolved),
    },
    {
        label: 'False Call',
        icon: 'i-ph-warning-circle-duotone',
        color: 'bg-red-600 hover:bg-red-500',
        click: () => onEndCall(CallStatus.falseCall),
    }
]]

const FIELD_LABELS: Record<string, string> = {
    call_reason_id: 'Reason for Calling',
    call_decision_id: 'Final Decision',
    diagnosis_id: 'Suspected Diagnosis',
    notes: 'Notes',
}

async function onEndCall(status: CallStatus) {
    try {
        await $fetch('/api/calls/end', {
            method: 'POST',
            body: {
                id: props.callId,
                status,
            },
        })

        emit('confirm')
    } catch (e: any) {
        const data = e?.data?.data ?? e?.data
        if (data?.code === 'CALL_REPORT_INCOMPLETE') {
            const labels = (data.missing as string[]).map((f) => FIELD_LABELS[f] ?? f).join(', ')
            toast.add({
                title: 'Complete the call report before resolving',
                description: `Missing: ${labels}`,
                color: 'orange',
                icon: 'i-ph-warning-duotone',
            })
            emit('report-incomplete', { reportId: data.reportId ?? null, missing: data.missing })
            return
        }
        throw e
    }
}
</script>